import { useMutation, useQueryClient } from "react-query";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { IAccessPermission } from "../../common/models/AccessPermission";
import { ObjectTagValuesConfigurationDescription, ObjectTagValuesConfigurations, ObjectTagValuesDefault } from "../../services/object-tag-values-data-service/ConfigurationModels";
import ObjectTagValuesDataService from "../../services/object-tag-values-data-service/ObjectTagValuesDataService";
import { RestError } from "../../services/rest-service/RestService";
import { BaseUseMutationParameters } from "../BaseUseMutationParameters";
import { MutationContextType } from "./Common";
import { keys } from "./Keys";

export type UseSaveHMIDefaultParameters = {
    groupID: number,
    objectType: eLynxObjectTypeEnum,
    objectID: number;
} & BaseUseMutationParameters<ObjectTagValuesDefault, ObjectTagValuesDefault>;

export function useSaveObjectTagValuesDefault({
    groupID,
    objectType,
    objectID,
    onError,
    onSuccess
}: UseSaveHMIDefaultParameters) {
    const queryClient = useQueryClient();

    return useMutation<ObjectTagValuesDefault, RestError, ObjectTagValuesDefault, MutationContextType>(
        async (model) => {
            await ObjectTagValuesDataService.setDefault(
                model,
                groupID,
                objectType,
                objectID
            );

            return Promise.resolve(model);
        },
        {
            onMutate: async (model: ObjectTagValuesDefault) => {

                await queryClient.cancelQueries(keys.all);

                const previousConfigurations = queryClient.getQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    )
                );

                queryClient.setQueryData<ObjectTagValuesConfigurations>(
                    keys.configurationList(
                        groupID,
                        objectType,
                        objectID
                    ),

                    (old?: ObjectTagValuesConfigurations) => {

                        return old
                            ? {
                                defaultConfigurationID: model.configurationID,
                                userPermissions: old.userPermissions,
                                configurations: old.configurations
                            }
                            : {
                                defaultConfigurationID: model.configurationID,
                                userPermissions: new Array<IAccessPermission>(),
                                configurations: new Array<ObjectTagValuesConfigurationDescription>()
                            };
                    }
                );

                return {
                    previousConfigurations
                };
            },
            onError: onError,
            onSuccess: onSuccess,
            onSettled: (_data, error, _item, context) => {

                if (error && context?.previousConfigurations) {
                    queryClient.setQueryData<ObjectTagValuesConfigurations>(
                        keys.configurationList(
                            groupID,
                            objectType,
                            objectID
                        ),
                        context.previousConfigurations
                    );
                }

                queryClient.invalidateQueries(keys.configurationLists());
            }
        }
    );
}